import { ChangeDetectorRef, Component, computed, effect, ElementRef, inject, input, OnInit, output, Signal, signal, ViewChild } from '@angular/core';
import { ApiSettings, PaginationConstants } from '../../../../core/constants/constants';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { Router } from '@angular/router';
import { ChargeMarkAction, ChargeStatus, PaymentStatus, SelectionPrimaryKeys } from '../../../../core/enums/data.enums';
import { ModalActions, ModalTypes, PropertyChargeListViewLocations, VoidBlockReason } from '../../../../core/enums/base.enums';
import { PaymentHistoryListSortOptions, PropertyChargeListSortOptions } from '../../../../core/enums/sort-options.enums';
import { PaginatedListModel } from '../../../../core/models/service/paginated-list.model';
import { ChargesService } from '../../../../core/services/charge/charge.service';
import { PaymentService } from '../../../../core/services/payment/payment.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { ProfileOrganizationScopeService } from '../../../../core/services/authorization/profile-organization-scope.service';
import { PermissionUtilities } from '../../../../core/utilities/permission.utilities';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { PropertyChargeModel, WaiveLateFeesBulkActionRequest, WaiveLateFeesRequest } from '../../../../core/models/data/property-charge.model';
import { BalancesMessagingConstants, PaymentMessagingConstants } from '../../../../core/constants/messaging.constants';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { PropertyModel } from '../../../../core/models/data/property.model';
import { PropertyChargeFilter } from '../../../../core/filters/property-charge.filter';
import { ItemSelectionPipe } from '../../../../core/pipes/item-selection/item-selection-pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InViewPortDirective } from '../../../../core/directives/in-view-port.directive';
import { PaginationComponent } from '../../../../parts/pagination/pagination.component';
import { SortDropdownComponent } from '../../../../parts/sort-dropdown/sort-dropdown/sort-dropdown';
import { NoValuePipe } from '../../../../core/pipes/no-value/no-value.pipe';
import { OrgDatePipe } from '../../../../core/pipes/org-date/org-date.pipe';
import { MobileCardCheckbox } from '../../../../parts/mobile-card-checkbox/mobile-card-checkbox';
import { BaseListPageClass } from '../../../../core/base-classes/base-list-page-class';
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { BulkActionRequest } from '../../../../core/models/service/bulk-action-request.model';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { PropertyChargeSearchFilter } from '../property-charge-search-filter/property-charge-search-filter';
import { CurrencyDisplayPipe } from '../../../../core/pipes/currency/currency.pipe';
import { ResponsiveTableDirective } from '../../../../core/directives/responsive-table/responsive-table.directive';
import { ResponsiveTableColumnDirective } from '../../../../core/directives/responsive-table/responsive-table-column.directive';
import { CurrencyModel } from '../../../../core/models/data/currency.model';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { BalancesPermission, Permission, PaymentsPermission } from '../../../../core/enums/permissions.enums';
import { MarkInvoiceModalComponent } from '../mark-invoice-modal/mark-invoice-modal.component';
import { InvoiceLogModalComponent } from '../invoice-log-modal/invoice-log-modal.component';
import { CloseMenuOnSettleDirective } from '../../../../core/directives/close-menu-on-settle.directive';
import { DebounceClickDirective } from '../../../../core/directives/debounce-click.directive';
import { FileDownloadUtilities } from '../../../../core/utilities/file-download.utilities';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';

interface PropertyChargeDisplayItem extends PropertyChargeModel {
  isSelectable: boolean;
  canMarkAsPaid: boolean;
  canVoid: boolean;
  canWaiveLateFees: boolean;
}

// Responsive hide order for the invoice table: the highest number is dropped first, 1 never drops.
interface PropertyChargeColumnPriorities {
  invoiceId: number;
  invoiceName: number;
  property: number;
  issued: number;
  due: number;
  status: number;
  owed: number;
  fees: number;
}

// DEV-2683: the member-facing card leads with due/status/amount; every other view keeps Property
// near the front because it spans properties.
const MEMBER_PROFILE_COLUMN_PRIORITIES: PropertyChargeColumnPriorities = {
  invoiceId: 1,
  due: 2,
  status: 3,
  owed: 4,
  invoiceName: 5,
  property: 6,
  issued: 7,
  fees: 8
};

const DEFAULT_COLUMN_PRIORITIES: PropertyChargeColumnPriorities = {
  invoiceId: 1,
  property: 2,
  due: 3,
  status: 4,
  owed: 5,
  invoiceName: 6,
  issued: 6,
  fees: 7
};

@Component({
  selector: 'ms-property-charge-list',
  imports: [
    FontAwesomeModule,
    PaginationComponent,
    FormsModule,
    CommonModule,
    InViewPortDirective,
    SortDropdownComponent,
    ItemSelectionPipe,
    NoValuePipe,
    OrgDatePipe,
    MobileCardCheckbox,
    StatusBadgeComponent,
    PropertyChargeSearchFilter,
    CurrencyDisplayPipe,
    ResponsiveTableDirective,
    ResponsiveTableColumnDirective,
    IfHasPermissionDirective,
    MarkInvoiceModalComponent,
    InvoiceLogModalComponent,
    ConfirmationModalComponent,
    InfoModalComponent,
    CloseMenuOnSettleDirective,
    DebounceClickDirective,
    TooltipDirective
],
  templateUrl: './property-charge-list.component.html',
  styleUrl: './property-charge-list.component.scss',
  host: { "style": "display:contents" }
})
export class PropertyChargeListComponent extends BaseListPageClass implements OnInit {

  //#region Properties & Signals
  public propertyChargeScheduleId = input<number | undefined>(undefined);
  public currentPropertyChargeListViewLocation = input<PropertyChargeListViewLocations>(PropertyChargeListViewLocations.PropertyChargeScheduleSummaryPage);
  public property = input<PropertyModel | undefined>(undefined);
  public memberId = input<number | undefined>(undefined);
  public scheduleOptions = input<{ id: number, name: string }[]>([]);

  public isProfileView: Signal<boolean> = computed(() =>
    this.currentPropertyChargeListViewLocation() === PropertyChargeListViewLocations.PropertyProfilePage ||
    this.currentPropertyChargeListViewLocation() === PropertyChargeListViewLocations.MemberProfilePage
  );

  public isMemberProfileView: Signal<boolean> = computed(() =>
    this.currentPropertyChargeListViewLocation() === PropertyChargeListViewLocations.MemberProfilePage
  );

  public isInvoiceDashboardView: Signal<boolean> = computed(() =>
    this.currentPropertyChargeListViewLocation() === PropertyChargeListViewLocations.InvoiceDashboardPage
  );

  public showInvoiceNameColumn: Signal<boolean> = computed(() => this.isProfileView() || this.isInvoiceDashboardView());

  public columnPriorities: Signal<PropertyChargeColumnPriorities> = computed(() =>
    this.isMemberProfileView() ? MEMBER_PROFILE_COLUMN_PRIORITIES : DEFAULT_COLUMN_PRIORITIES
  );

  public hideBulkActions: Signal<boolean> = computed(() => this.isInvoiceDashboardView());

  public defaultFilterFactory: Signal<(() => PropertyChargeFilter) | undefined> = computed(() =>
    this.isInvoiceDashboardView()
      ? ListConstants.PROPERTY_CHARGES_INVOICE_DASHBOARD_FILTER_DEFAULT
      : undefined
  );

  public propertyChargeList = signal<PropertyChargeModel[]>([]);
  public chargesWithActionFlags = computed<PropertyChargeDisplayItem[]>(() =>
    this.propertyChargeList().map(charge => ({
      ...charge,
      isSelectable: charge.status !== ChargeStatus.Locked && charge.status !== ChargeStatus.Voided,
      canMarkAsPaid: charge.status === ChargeStatus.Open || charge.status === ChargeStatus.Late,
      canVoid: charge.status === ChargeStatus.Open || charge.status === ChargeStatus.Late || charge.status === ChargeStatus.Locked
        || PropertyChargeListComponent.isPaidStatus(charge.status),
      canWaiveLateFees: (charge.status === ChargeStatus.Open || charge.status === ChargeStatus.Late) && (charge.totalLateFees ?? 0) > 0
    }))
  );
  public showBulkWaiveLateFees = computed(() => this.hasWaiveableSelection());
  public totalOwedAmount = computed(() => this.propertyChargeList().filter(charge => charge.status !== ChargeStatus.Voided && !charge.paidDate).reduce((sum, charge) => sum + (charge.amount ?? 0) + (charge.totalLateFees ?? 0), 0));
  public totalSelectedAmount = computed(() => this.calculateAmountSelected());
  public totalSelectedAmountChange = output<number>();
  public totalCurrencyChange = output<CurrencyModel | undefined>();
  public payRequested = output<void>();
  public filterChanged = output<PropertyChargeFilter>();
  public totalCurrency = computed(() => this.propertyChargeList()[0]?.currency);
  private profileScope = inject(ProfileOrganizationScopeService, { optional: true });

  // The organization this list belongs to: the profile entity's own when rendered on a member or
  // property profile, otherwise the active one.
  private scopedOrganizationId(): number | undefined {
    return PermissionUtilities.resolveOrganizationScope(undefined, this.profileScope, this.userSessionService).organizationId;
  }

  public isStripeOnboardingComplete = computed(() =>
    ProfileOrganizationScopeService.resolveSettings(this.profileScope, this.userSessionService)?.isStripeOnboardingComplete ?? false);
  public onlinePaymentsDisabledTooltip = PaymentMessagingConstants.PAYMENT_ONLINE_PAYMENTS_DISABLED_TOOLTIP();
  public propertyChargeFilter: PropertyChargeFilter = ListConstants.PROPERTY_CHARGES_FILTER_DEFAULT();
  public ChargeStatus = ChargeStatus;

  public PropertyChargeListViewLocations = PropertyChargeListViewLocations;
  public BalancesPermissions = BalancesPermission;
  public PaymentPermissions = PaymentsPermission;

  public propertyChargeListSortOptions = PropertyChargeListSortOptions;

  public skeletonCharges: PropertyChargeModel[] = [];

  public SelectionPrimaryKeys = SelectionPrimaryKeys;
  public downloadingInvoiceChargeId = signal<number | null>(null);
  public resendingInvoiceChargeId = signal<number | null>(null);
  public isExportingFilteredData = signal<boolean>(false);
  public isWaivingLateFees = signal<boolean>(false);
  public isBulkResendingInvoices = signal<boolean>(false);
  public resolvingVoidBlockChargeId = signal<number | null>(null);

  private waiveMode = signal<'individual' | 'bulk' | null>(null);
  private waiveTargetCharge = signal<PropertyChargeModel | null>(null);

  @ViewChild('markInvoiceModalRef') markInvoiceModalRef!: MarkInvoiceModalComponent;
  @ViewChild('invoiceLogModalRef') invoiceLogModalRef!: InvoiceLogModalComponent;
  @ViewChild('waiveLateFeesConfirmationModalRef') waiveLateFeesConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('waiveLateFeesInfoModalRef') waiveLateFeesInfoModalRef!: InfoModalComponent;
  @ViewChild('resendInvoiceInfoModalRef') resendInvoiceInfoModalRef!: InfoModalComponent;
  @ViewChild('bulkResendInvoicesConfirmationModalRef') bulkResendInvoicesConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('bulkResendInvoicesInfoModalRef') bulkResendInvoicesInfoModalRef!: InfoModalComponent;
  @ViewChild('voidBlockedConfirmationModalRef') voidBlockedConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('voidBlockedInfoModalRef') voidBlockedInfoModalRef!: InfoModalComponent;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;

  constructor(
    private chargesService: ChargesService,
    private paymentService: PaymentService,
    private changeDetector: ChangeDetectorRef,
    private bootstrapService: BootstrapService,
    private authorizationService: AuthorizationService,
    private authorizationStateService: AuthorizationStateService,
    private routerService: Router,
    public userSessionService: UserSessionService
  ) {
    super();

    effect(() => {
      this.totalSelectedAmountChange.emit(this.totalSelectedAmount());
      this.totalCurrencyChange.emit(this.totalCurrency());
    });

  }

  public async ngOnInit(): Promise<void> {
    this.sortOptions = [...ListConstants.PROPERTY_CHARGES_SORT_OPTIONS()];
    this.propertyChargeFilter = ListConstants.PROPERTY_CHARGES_FILTER_DEFAULT();

    if (this.currentPropertyChargeListViewLocation() === PropertyChargeListViewLocations.PropertyProfilePage) {
      this.take = PaginationConstants.PAGINATION_MAX_PAGE_SIZE();
      this.orderBy = PropertyChargeListSortOptions.DueAsc;
      this.propertyChargeFilter.statuses = [ChargeStatus.Locked, ChargeStatus.Open, ChargeStatus.Late, ChargeStatus.Voided];
      if (this.property()?.propertyId) {
        this.propertyChargeFilter.propertyIds = [this.property()!.propertyId];
      }
    } else if (this.currentPropertyChargeListViewLocation() === PropertyChargeListViewLocations.MemberProfilePage) {
      this.take = PaginationConstants.PAGINATION_MAX_PAGE_SIZE();
      this.orderBy = PropertyChargeListSortOptions.DueAsc;
      this.propertyChargeFilter.statuses = [ChargeStatus.Locked, ChargeStatus.Open, ChargeStatus.Late, ChargeStatus.Voided];
      if (this.memberId()) {
        this.propertyChargeFilter.memberIds = [this.memberId()!];
      }
    } else if (this.currentPropertyChargeListViewLocation() === PropertyChargeListViewLocations.InvoiceDashboardPage) {
      this.orderBy = PropertyChargeListSortOptions.DueAsc;
      this.propertyChargeFilter = ListConstants.PROPERTY_CHARGES_INVOICE_DASHBOARD_FILTER_DEFAULT();
    } else {
      this.orderBy = ListConstants.PROPERTY_CHARGES_ORDERBY_DEFAULT();
      if (this.propertyChargeScheduleId()) {
        this.propertyChargeFilter.propertyChargeScheduleIds = [this.propertyChargeScheduleId()!];
      }
    }

    // The organization on screen, not the active one. On a member or property profile opened for an
    // organization other than the active one, sending the active organization's id alongside the
    // correct memberId/propertyId matched no charges and the Invoicing tab rendered empty (DEV-2643).
    const organizationId = this.scopedOrganizationId();
    if (organizationId !== undefined) {
      this.propertyChargeFilter.organizationIds = [organizationId];
    }

    this.createSkeletonCharges();
    await this.loadPropertyCharges(false);

    if (this.isProfileView()) {
      this.toggleSelectAll();
    }
  }

  private createSkeletonCharges(): void {
    this.skeletonCharges = Array.from({ length: this.take }, (_, index) => ({
      propertyChargeId: index,
      propertyChargeScheduleId: -1,
      propertyId: -1,
      name: 'Loading...',
      description: 'Loading...',
      amount: 0,
      currency: { currencyId: 1, code: 'USD', name: 'USD', symbol: '$', minorUnits: 100 },
      issuedDate: '2025-01-01',
      dueDate: '2025-01-01',
      status: ChargeStatus.Open,
      propertyTitle: 'Loading...',
      primaryAddress: 'Loading...',
      lotNumber: 'Loading...'
    } as PropertyChargeModel));
  }

  //#endregion Properties & Signals

  //#region Data Loading
  public get displayPropertyCharges(): PropertyChargeDisplayItem[] {
    if (this.isLoading && (!this.propertyChargeList() || this.propertyChargeList().length === 0)) {
      return this.skeletonCharges as PropertyChargeDisplayItem[];
    }
    return this.chargesWithActionFlags();
  }

  public async loadPropertyCharges(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (append === false) { this.resetPagination(); }

    if (this.propertyChargeScheduleId()) {
      this.propertyChargeFilter.propertyChargeScheduleIds = [this.propertyChargeScheduleId()!];
    }

    const orgSettings = await this.userSessionService.getOrganizationSettings();
    this.propertyChargeFilter.timeZoneId = 
      orgSettings?.timeZoneId ?? ApiSettings.DEFAULT_ORG_TIMEZONE_FALLBACK().id;

    const take = this.isProfileView() ? PaginationConstants.PAGINATION_MAX_PAGE_SIZE() : this.take;

    const response: ServiceResultModel<PaginatedListModel<PropertyChargeModel>> =
      await this.chargesService.searchPropertyCharges(this.propertyChargeFilter, this.orderBy, this.skip(), take);

    this.totalCount = response?.data?.total ?? 0;
    this.hasNext = response?.data?.hasNext ?? false;

    if (response?.data?.data) {
      if (append === true) {
        this.propertyChargeList.set(this.propertyChargeList().concat(response.data.data));
      } else {
        this.propertyChargeList.set(response.data.data);
        this.isSelectAll = false;
        this.selectionItems.set([]);
      }
    }

    this.isLoading = false;
    this.calculateSelected();
    this.changeDetector.detectChanges();

    if (append === false) {
      this.filterChanged.emit({ ...this.propertyChargeFilter });
    }
  }

  public async loadMore(): Promise<void> {
    await this.loadPropertyCharges(true);
  }

  public filterApplied(): void {
    this.loadPropertyCharges();
  }

  public applyExternalFilter(patch: Partial<PropertyChargeFilter>): void {
    Object.assign(this.propertyChargeFilter, patch);
    this.filterApplied();
  }

  public loadMoreInView(): void {
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }

  public clearSearch(): void {
    this.propertyChargeFilter.searchTerm = "";
  }
  //#endregion Data Loading

  //#region Sorting & Pagination
  public isPropertyColumnSortActive(): boolean {
    return [
      PropertyChargeListSortOptions.PropertyTitleAsc,
      PropertyChargeListSortOptions.PropertyTitleDesc,
      PropertyChargeListSortOptions.PrimaryAddressAsc,
      PropertyChargeListSortOptions.PrimaryAddressDesc,
      PropertyChargeListSortOptions.LotNumberAsc,
      PropertyChargeListSortOptions.LotNumberDesc
    ].includes(this.orderBy as PropertyChargeListSortOptions);
  }

  public isPropertyColumnSortAsc(): boolean {
    return [
      PropertyChargeListSortOptions.PropertyTitleAsc,
      PropertyChargeListSortOptions.PrimaryAddressAsc,
      PropertyChargeListSortOptions.LotNumberAsc
    ].includes(this.orderBy as PropertyChargeListSortOptions);
  }

  public isPropertyColumnSortDesc(): boolean {
    return [
      PropertyChargeListSortOptions.PropertyTitleDesc,
      PropertyChargeListSortOptions.PrimaryAddressDesc,
      PropertyChargeListSortOptions.LotNumberDesc
    ].includes(this.orderBy as PropertyChargeListSortOptions);
  }

  public toggleSortDirection(columnName: string): void {
    const columnSortMap: Record<string, [PropertyChargeListSortOptions, PropertyChargeListSortOptions]> = {
      "Invoice #": [PropertyChargeListSortOptions.PropertyChargeIdAsc, PropertyChargeListSortOptions.PropertyChargeIdDesc],
      "Property Title": [PropertyChargeListSortOptions.PropertyTitleAsc, PropertyChargeListSortOptions.PropertyTitleDesc],
      "Address": [PropertyChargeListSortOptions.PrimaryAddressAsc, PropertyChargeListSortOptions.PrimaryAddressDesc],
      "Lot #": [PropertyChargeListSortOptions.LotNumberAsc, PropertyChargeListSortOptions.LotNumberDesc],
      "Issued Date": [PropertyChargeListSortOptions.IssuedAsc, PropertyChargeListSortOptions.IssuedDesc],
      "Due Date": [PropertyChargeListSortOptions.DueAsc, PropertyChargeListSortOptions.DueDesc],
      "Status": [PropertyChargeListSortOptions.StatusAsc, PropertyChargeListSortOptions.StatusDesc],
      "Amount Owed": [PropertyChargeListSortOptions.OwedAmountAsc, PropertyChargeListSortOptions.OwedAmountDesc],
      "Amount Paid": [PropertyChargeListSortOptions.PaidAmountAsc, PropertyChargeListSortOptions.PaidAmountDesc],
      "Date Paid": [PropertyChargeListSortOptions.PaidDateAsc, PropertyChargeListSortOptions.PaidDateDesc],
      "Name": [PropertyChargeListSortOptions.NameAsc, PropertyChargeListSortOptions.NameDesc],
      "Late Fees": [PropertyChargeListSortOptions.LateFeesAsc, PropertyChargeListSortOptions.LateFeesDesc],
    };

    const [ascOption, descOption] = columnSortMap[columnName]
      || [PropertyChargeListSortOptions.PropertyChargeIdAsc, PropertyChargeListSortOptions.PropertyChargeIdDesc];

    let sortDirection: PropertyChargeListSortOptions;

    if (this.orderBy === descOption) {
      sortDirection = ascOption;
    } else if (this.orderBy === ascOption) {
      sortDirection = descOption;
    } else {
      sortDirection = ascOption;
    }

    this.setSortOrder(sortDirection);
  }

  public setSortOrder(sortOption: string) {
    this.orderBy = sortOption;
    this.filterApplied();
  }

  public jumpToTop(element: string): void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;

      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }

  //#endregion Sorting & Pagination

  //#region Actions

  public markAsPaid(charge: PropertyChargeModel): void {
    this.markInvoiceModalRef.open(charge, ChargeMarkAction.Paid);
  }

  public async voidInvoice(charge: PropertyChargeModel): Promise<void> {
    if (PropertyChargeListComponent.isPaidStatus(charge.status)) {
      await this.showVoidBlocked(charge);
      return;
    }

    this.markInvoiceModalRef.open(charge, ChargeMarkAction.Void);
  }

  public goToPaymentDashboard(): void {
    this.bootstrapService.manageModal('#invoiceVoidBlockedModal', ModalActions.Hide);
    this.routerService.navigate(['/payment-dashboard']);
  }

  public dismissVoidBlocked(): void {
    this.bootstrapService.manageModal('#invoiceVoidBlockedModal', ModalActions.Hide);
  }

  public voidBlockedInfoConfirmed(): void {
    this.bootstrapService.manageModal('#invoiceVoidBlockedInfoModal', ModalActions.Hide);
  }

  public openInvoiceLog(charge: PropertyChargeModel): void {
    this.invoiceLogModalRef.open(charge);
  }

  public onMarkCompleted(updatedCharge: PropertyChargeModel): void {
    const currentList = this.propertyChargeList();
    const index = currentList.findIndex(c => c.propertyChargeId === updatedCharge.propertyChargeId);
    if (index !== -1) {
      const updatedList = [...currentList];

      const statuses = this.propertyChargeFilter?.statuses;
      const hasActiveStatusFilter = Array.isArray(statuses) && statuses.length > 0;
      const statusFilteredOut = hasActiveStatusFilter && !statuses!.includes(updatedCharge.status);

      if (statusFilteredOut) {
        updatedList.splice(index, 1);
        this.totalCount = Math.max(0, this.totalCount - 1);
      } else {
        updatedList[index] = updatedCharge;
      }

      this.propertyChargeList.set(updatedList);
    }
  }

  public lockInvoice(charge: PropertyChargeModel): void {
    console.log('Lock Invoice - Placeholder', charge.propertyChargeId);
  }

  public addNotes(charge: PropertyChargeModel): void {
    console.log('Add Notes - Placeholder', charge.propertyChargeId);
  }

  // Drives msCloseMenuOnSettle: a row's menu offers all three actions, so any one of them closes it.
  public isChargeActionRunning(charge: PropertyChargeModel): boolean {
    return this.downloadingInvoiceChargeId() === charge.propertyChargeId
      || this.resendingInvoiceChargeId() === charge.propertyChargeId
      || this.resolvingVoidBlockChargeId() === charge.propertyChargeId;
  }

  public async downloadInvoice(charge: PropertyChargeModel): Promise<void> {
    if (this.downloadingInvoiceChargeId() !== null) return;

    this.downloadingInvoiceChargeId.set(charge.propertyChargeId);

    try {
      const response = await this.chargesService.downloadInvoicePdf(charge.propertyChargeId);

      if (response.success && response.data) {
        const filename = FileDownloadUtilities.getFilenameFromContentDisposition(response.message ?? null) || `Invoice_${charge.propertyChargeId}.pdf`;

        FileDownloadUtilities.downloadBlob(response.data, filename);
      }
    } finally {
      this.downloadingInvoiceChargeId.set(null);
    }
  }

  public async resendInvoice(charge: PropertyChargeModel): Promise<void> {
    if (this.resendingInvoiceChargeId() !== null) return;

    this.resendingInvoiceChargeId.set(charge.propertyChargeId);

    try {
      const response = await this.chargesService.resendInvoice(charge.propertyChargeId);
      this.showResendInvoiceResult(
        response.success === true,
        response.message ?? BalancesMessagingConstants.RESEND_INVOICE_SUCCESS(),
        response.message ?? BalancesMessagingConstants.RESEND_INVOICE_FAILURE()
      );
    } finally {
      this.resendingInvoiceChargeId.set(null);
    }
  }

  private showResendInvoiceResult(success: boolean, successMessage: string, failureMessage: string): void {
    this.resendInvoiceInfoModalRef.modalType.set(success ? ModalTypes.Success : ModalTypes.Failure);
    this.resendInvoiceInfoModalRef.message.set(success ? successMessage : failureMessage);
    this.bootstrapService.manageModal('#resendInvoiceInfoModal', ModalActions.Show);
  }

  public async exportFilteredData(): Promise<void> {
    if (this.isExportingFilteredData()) return;

    this.isExportingFilteredData.set(true);
    try {
      const response = await this.chargesService.exportChargeBulkData({
        useFilter: true,
        filter: { ...this.propertyChargeFilter },
        selectedIds: null,
        excludedIds: null,
        note: null
      });

      if (response.success && response.data) {
        FileDownloadUtilities.downloadBlob(response.data, FileDownloadUtilities.buildInvoiceDataFilename());
      }
    } finally {
      this.isExportingFilteredData.set(false);
    }
  }

  //#endregion Actions

  //#region Bulk Actions

  public markSelectedAsPaid(): void {
    const selectedIds = this.getSelectedIds();
    console.log('Mark Selected as Paid - Placeholder', selectedIds);
  }

  public voidSelectedInvoices(): void {
    const selectedIds = this.getSelectedIds();
    console.log('Void Selected Invoices - Placeholder', selectedIds);
  }

  public lockSelectedInvoices(): void {
    const selectedIds = this.getSelectedIds();
    console.log('Lock Selected Invoices - Placeholder', selectedIds);
  }

  public resendSelectedInvoices(): void {
    if (this.selectedCount() === 0 || this.isBulkResendingInvoices()) { return; }

    const message = BalancesMessagingConstants.BULK_RESEND_INVOICES_CONFIRMATION()
      .replace('{selectedCount}', String(this.selectedCount()));

    this.bulkResendInvoicesConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.bulkResendInvoicesConfirmationModalRef.title.set('Resend Invoices');
    this.bulkResendInvoicesConfirmationModalRef.message.set(message);
    this.bulkResendInvoicesConfirmationModalRef.enableNotes.set(false);
    this.bulkResendInvoicesConfirmationModalRef.confirmButtonText.set('Confirm');

    this.bootstrapService.manageModal('#bulkResendInvoicesConfirmationModal', ModalActions.Show);
  }

  public async onBulkResendInvoicesConfirmed(): Promise<void> {
    if (this.isBulkResendingInvoices()) { return; }
    this.isBulkResendingInvoices.set(true);
    this.bulkResendInvoicesConfirmationModalRef.isSubmitting.set(true);

    try {
      const request = this.buildChargeBulkActionRequest();
      const response = await this.chargesService.bulkResendInvoices(request);

      this.bootstrapService.manageModal('#bulkResendInvoicesConfirmationModal', ModalActions.Hide);

      if (response.success) {
        this.bulkResendInvoicesInfoModalRef.modalType.set(ModalTypes.Success);
        this.bulkResendInvoicesInfoModalRef.message.set(
          response.message ?? BalancesMessagingConstants.BULK_RESEND_INVOICES_SUCCESS()
        );
        this.isSelectAll = false;
        this.selectionItems.set([]);
        this.calculateSelected();
      } else {
        this.bulkResendInvoicesInfoModalRef.modalType.set(ModalTypes.Failure);
        this.bulkResendInvoicesInfoModalRef.message.set(
          response.message ?? BalancesMessagingConstants.BULK_RESEND_INVOICES_FAILURE()
        );
      }
      this.bootstrapService.manageModal('#bulkResendInvoicesInfoModal', ModalActions.Show);
    } finally {
      this.isBulkResendingInvoices.set(false);
      this.bulkResendInvoicesConfirmationModalRef.isSubmitting.set(false);
    }
  }

  public onBulkResendInvoicesCancelled(): void {
    this.bootstrapService.manageModal('#bulkResendInvoicesConfirmationModal', ModalActions.Hide);
  }

  public waiveLateFees(charge: PropertyChargeModel): void {
    this.waiveMode.set('individual');
    this.waiveTargetCharge.set(charge);

    const message = BalancesMessagingConstants.WAIVE_LATE_FEES_INDIVIDUAL_CONFIRMATION()
      .replace('{invoiceId}', String(charge.propertyChargeId));

    this.waiveLateFeesConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.waiveLateFeesConfirmationModalRef.title.set('Waive Late Fees');
    this.waiveLateFeesConfirmationModalRef.message.set(message);
    this.waiveLateFeesConfirmationModalRef.enableNotes.set(true);
    this.waiveLateFeesConfirmationModalRef.confirmButtonText.set('Confirm');

    this.bootstrapService.manageModal('#waiveLateFeesConfirmationModal', ModalActions.Show);
  }

  public waiveSelectedLateFees(): void {
    if (!this.hasWaiveableSelection()) { return; }

    this.waiveMode.set('bulk');
    this.waiveTargetCharge.set(null);

    const message = BalancesMessagingConstants.WAIVE_LATE_FEES_BULK_CONFIRMATION()
      .replace('{selectedCount}', String(this.selectedCount()));

    this.waiveLateFeesConfirmationModalRef.modalType.set(ModalTypes.Question);
    this.waiveLateFeesConfirmationModalRef.title.set('Waive Late Fees');
    this.waiveLateFeesConfirmationModalRef.message.set(message);
    this.waiveLateFeesConfirmationModalRef.enableNotes.set(true);
    this.waiveLateFeesConfirmationModalRef.confirmButtonText.set('Confirm');

    this.bootstrapService.manageModal('#waiveLateFeesConfirmationModal', ModalActions.Show);
  }

  public async onWaiveConfirmed(note: string | undefined): Promise<void> {
    const mode = this.waiveMode();
    if (!mode) { return; }

    this.isWaivingLateFees.set(true);
    this.waiveLateFeesConfirmationModalRef.isSubmitting.set(true);

    try {
      if (mode === 'individual') {
        await this.processIndividualWaive(note);
      } else {
        await this.processBulkWaive(note);
      }
    } finally {
      this.isWaivingLateFees.set(false);
      this.waiveLateFeesConfirmationModalRef.isSubmitting.set(false);
      this.waiveMode.set(null);
      this.waiveTargetCharge.set(null);
    }
  }

  public onWaiveCancelled(): void {
    this.bootstrapService.manageModal('#waiveLateFeesConfirmationModal', ModalActions.Hide);
    this.waiveMode.set(null);
    this.waiveTargetCharge.set(null);
  }

  private async processIndividualWaive(note: string | undefined): Promise<void> {
    const charge = this.waiveTargetCharge();
    if (!charge) { return; }

    const request: WaiveLateFeesRequest = {
      note: note?.trim() || undefined,
      sendEmail: true
    };

    const response = await this.chargesService.waiveLateFees(charge.propertyChargeId, request);

    this.bootstrapService.manageModal('#waiveLateFeesConfirmationModal', ModalActions.Hide);

    if (response.success && response.data) {
      this.onMarkCompleted(response.data);
      this.waiveLateFeesInfoModalRef.modalType.set(ModalTypes.Success);
      this.waiveLateFeesInfoModalRef.message.set(response.message ?? BalancesMessagingConstants.WAIVE_LATE_FEES_SUCCESS().replace('{invoiceId}', String(charge.propertyChargeId)));
    } else {
      this.waiveLateFeesInfoModalRef.modalType.set(ModalTypes.Failure);
      this.waiveLateFeesInfoModalRef.message.set(response.message ?? BalancesMessagingConstants.WAIVE_LATE_FEES_FAILURE());
    }
    this.bootstrapService.manageModal('#waiveLateFeesInfoModal', ModalActions.Show);
  }

  private async processBulkWaive(note: string | undefined): Promise<void> {
    const base = this.buildChargeBulkActionRequest();
    const request: WaiveLateFeesBulkActionRequest = {
      ...base,
      note: note?.trim() || null,
      sendEmail: true
    };

    const response = await this.chargesService.bulkWaiveLateFees(request);

    this.bootstrapService.manageModal('#waiveLateFeesConfirmationModal', ModalActions.Hide);

    if (response.success) {
      this.waiveLateFeesInfoModalRef.modalType.set(ModalTypes.Success);
      this.waiveLateFeesInfoModalRef.message.set(response.message ?? BalancesMessagingConstants.WAIVE_LATE_FEES_BULK_SUCCESS());
      this.isSelectAll = false;
      this.selectionItems.set([]);
      await this.loadPropertyCharges(false);
    } else {
      this.waiveLateFeesInfoModalRef.modalType.set(ModalTypes.Failure);
      this.waiveLateFeesInfoModalRef.message.set(response.message ?? BalancesMessagingConstants.WAIVE_LATE_FEES_FAILURE());
    }
    this.bootstrapService.manageModal('#waiveLateFeesInfoModal', ModalActions.Show);
  }

  public buildChargeBulkActionRequest(): BulkActionRequest<PropertyChargeFilter> {
    if (this.isSelectAll) {
      const unselectableIds = this.chargesWithActionFlags()
        .filter(charge => !charge.isSelectable)
        .map(charge => charge.propertyChargeId);

      const excludedIds = [...new Set([...this.selectionItems(), ...unselectableIds])];

      return {
        useFilter: true,
        excludedIds,
        filter: this.propertyChargeFilter,
        selectedIds: null,
        note: null
      };
    } else {
      return {
        useFilter: false,
        selectedIds: [...this.selectionItems()],
        filter: null,
        excludedIds: null,
        note: null
      };
    }
  }

  private getSelectedIds(): number[] {
    if (this.isSelectAll) {
      return this.chargesWithActionFlags()
        .filter(charge => charge.isSelectable)
        .map(charge => charge.propertyChargeId)
        .filter(id => !this.selectionItems().includes(id));
    } else {
      return [...this.selectionItems()];
    }
  }

  //#endregion Bulk Actions

  //#region Helper Methods

  private static isPaidStatus(status: ChargeStatus): boolean {
    return status === ChargeStatus.Paid || status === ChargeStatus.PaidLate;
  }

  // The invoice carries no payment-origin flag, so the copy is chosen from the payment behind it —
  // fetched on click rather than per row, which would be an N+1 across the page.
  private async showVoidBlocked(charge: PropertyChargeModel): Promise<void> {
    this.resolvingVoidBlockChargeId.set(charge.propertyChargeId);

    let reason: VoidBlockReason;
    try {
      reason = await this.resolveVoidBlockReason(charge.propertyChargeId);
    } finally {
      this.resolvingVoidBlockChargeId.set(null);
    }

    const canReachPaymentDashboard = this.hasOrganizationPermission(PaymentsPermission.Read);
    const message = PropertyChargeListComponent.voidBlockedMessage(reason, canReachPaymentDashboard);

    if (canReachPaymentDashboard) {
      this.voidBlockedConfirmationModalRef.modalType.set(ModalTypes.Information);
      this.voidBlockedConfirmationModalRef.message.set(message);
      this.bootstrapService.manageModal('#invoiceVoidBlockedModal', ModalActions.Show);
      return;
    }

    // Nowhere to send them: /payment-dashboard would only bounce off permissionsGuard to /system/403,
    // so this states the blocker rather than offering an action.
    this.voidBlockedInfoModalRef.modalType.set(ModalTypes.Information);
    this.voidBlockedInfoModalRef.message.set(message);
    this.bootstrapService.manageModal('#invoiceVoidBlockedInfoModal', ModalActions.Show);
  }

  private static voidBlockedMessage(reason: VoidBlockReason, canReachPaymentDashboard: boolean): string {
    switch (reason) {
      case VoidBlockReason.Refund:
        return canReachPaymentDashboard
          ? BalancesMessagingConstants.MARK_INVOICE_VOID_BLOCKED_REFUND()
          : BalancesMessagingConstants.MARK_INVOICE_VOID_BLOCKED_REFUND_NO_ACCESS();
      case VoidBlockReason.Unmark:
        return canReachPaymentDashboard
          ? BalancesMessagingConstants.MARK_INVOICE_VOID_BLOCKED_UNMARK()
          : BalancesMessagingConstants.MARK_INVOICE_VOID_BLOCKED_UNMARK_NO_ACCESS();
      default:
        return canReachPaymentDashboard
          ? BalancesMessagingConstants.MARK_INVOICE_VOID_BLOCKED_UNKNOWN()
          : BalancesMessagingConstants.MARK_INVOICE_VOID_BLOCKED_UNKNOWN_NO_ACCESS();
    }
  }

  // Statuses are narrowed to the ones that still hold the invoice paid, so a superseded record — an
  // earlier Unmarked or Refunded payment on the same invoice — cannot pick the copy.
  private async resolveVoidBlockReason(propertyChargeId: number): Promise<VoidBlockReason> {
    const result = await this.paymentService.searchPaymentHistory(
      {
        propertyChargeIds: [propertyChargeId],
        statuses: [
          PaymentStatus.Succeeded,
          PaymentStatus.Marked,
          PaymentStatus.Pending,
          PaymentStatus.RefundPending,
          PaymentStatus.Canceling
        ]
      },
      PaymentHistoryListSortOptions.DatePaidDesc,
      0,
      1
    );

    // The service swallows HTTP errors into success: false with no data, which is not the same as a
    // successful empty result — naming a payment type here would send the reader to do the one thing
    // they cannot.
    if (!result.success) return VoidBlockReason.Unknown;

    // No live payment at all: the invoice was imported or migrated from the legacy platform, both of
    // which land as marked paid.
    const isManualPayment = result.data?.data?.[0]?.isManualPayment ?? true;

    return isManualPayment ? VoidBlockReason.Unmark : VoidBlockReason.Refund;
  }

  private hasOrganizationPermission(permission: Permission): boolean {
    const authorizationState = this.authorizationStateService.authorizationState();
    if (!authorizationState) return false;

    return PermissionUtilities.hasScopedOrganizationPermission(
      permission, authorizationState, this.authorizationService, this.profileScope, this.userSessionService
    );
  }

  public override calculateSelected(): void {
    const unselectableCount = this.chargesWithActionFlags()
      .filter(charge => !charge.isSelectable).length;

    if (this.isSelectAll) {
      this.selectedCount.set(this.totalCount - unselectableCount - this.selectionItems().length);
    } else {
      this.selectedCount.set(this.selectionItems().length);
    }
  }

  public calculateAmountSelected(): number {
    // Read selectedCount() to ensure this computed re-evaluates on toggleSelectAll
    // (isSelectAll is a plain boolean, not a signal, so it can't trigger reactivity)
    const count = this.selectedCount();
    if (count === 0) return 0;

    const selectableCharges = this.chargesWithActionFlags()
      .filter(charge => charge.isSelectable);
    const exceptions = this.selectionItems();

    // The button label is a pre-service-fee preview — base amount + late fees only.
    // The payment-summary endpoint adds service fees once the modal opens.
    if (this.isSelectAll) {
      return selectableCharges
        .filter(pcl => !exceptions.includes(pcl.propertyChargeId))
        .reduce((sum, charge) => sum + (charge.amount ?? 0) + (charge.totalLateFees ?? 0), 0);
    } else {
      return selectableCharges
        .filter(pcl => exceptions.includes(pcl.propertyChargeId))
        .reduce((sum, charge) => sum + (charge.amount ?? 0) + (charge.totalLateFees ?? 0), 0);
    }
  }

  // Returns true when the current selection contains at least one charge that is eligible for late-fee waive.
  // When isSelectAll is true the explicit selection spans the full filtered dataset (including off-page rows),
  // so we trust the server to skip ineligible charges instead of hiding the action based on the loaded page.
  // Reading selectedCount() ensures the computed re-evaluates on toggleSelectAll (isSelectAll is a plain boolean).
  public hasWaiveableSelection(): boolean {
    if (this.selectedCount() === 0) return false;
    if (this.isSelectAll) return true;

    const waiveableCharges = this.chargesWithActionFlags().filter(charge => charge.canWaiveLateFees);
    const exceptions = this.selectionItems();
    return waiveableCharges.some(charge => exceptions.includes(charge.propertyChargeId));
  }

  //#endregion Helper Methods
}
